/**
 * Atajos de operación rápida en el InicioPage, ramificados por rol.
 *
 * Cada rol ve un set distinto:
 *   - staff           → Reservas (las suyas), Calendario, Mi perfil
 *   - center_admin    → Reservas, Calendario, Clientes, Staff, Servicios, Configuración
 *   - superadmin      → Centros, Horarios, Informes, Sistema, Reservas
 *
 * Si en el futuro hay que personalizar más fino (ej. center_admin con
 * varios centros añade "Cambiar centro"), extender el array por rol —
 * NO añadir condicionales dentro del render.
 */
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@components/Icons';

export type Role = 'staff' | 'center_admin' | 'superadmin';

type IconName =
  | 'Book'
  | 'Calendar'
  | 'Users'
  | 'Gear'
  | 'Building'
  | 'Storefront'
  | 'Chart'
  | 'Pulse'
  | 'List';

interface ShortcutDef {
  key: string;
  to: string;
  icon: IconName;
  title: string;
  hint: string;
}

const ALL_SHORTCUTS: Record<string, ShortcutDef> = {
  bookings:    { key: 'bookings',    to: '/admin/bookings',    icon: 'Book',       title: 'Reservas',       hint: 'Buscar, filtrar, exportar CSV' },
  calendar:    { key: 'calendar',    to: '/admin/calendar',    icon: 'Calendar',   title: 'Calendario',     hint: 'Vista semanal, bloqueos' },
  customers:   { key: 'customers',   to: '/admin/customers',   icon: 'Users',      title: 'Clientes',       hint: 'Buscar y gestionar fichas' },
  staff:       { key: 'staff',       to: '/admin/staff',       icon: 'Users',      title: 'Profesionales',  hint: 'Gestionar perfiles del equipo' },
  services:    { key: 'services',    to: '/admin/services',    icon: 'Storefront', title: 'Servicios',      hint: 'Catálogo y precios' },
  settings:    { key: 'settings',    to: '/admin/settings',    icon: 'Gear',       title: 'Configuración',  hint: 'Horario, modo confirmación, salas' },
  profile:     { key: 'profile',     to: '/admin/settings',    icon: 'Gear',       title: 'Mi perfil',      hint: 'Tus datos personales' },
  centers:     { key: 'centers',     to: '/admin/centers',     icon: 'Building',   title: 'Centros',        hint: 'Gestionar la red de centros' },
  horarios:    { key: 'horarios',    to: '/admin/horarios',    icon: 'Calendar',   title: 'Horarios',       hint: 'Plantillas semanales por staff' },
  informes:    { key: 'informes',    to: '/admin/informes',    icon: 'Chart',      title: 'Informes',       hint: 'Tendencias por centro / día' },
  sistema:     { key: 'sistema',     to: '/admin/sistema',     icon: 'Pulse',      title: 'Sistema',        hint: 'Salud, crons, backups' },
};

const SHORTCUTS_BY_ROLE: Record<Role, readonly string[]> = {
  staff:        ['bookings', 'calendar', 'profile'],
  center_admin: ['bookings', 'calendar', 'customers', 'staff', 'services', 'settings'],
  superadmin:   ['centers', 'horarios', 'informes', 'sistema', 'bookings'],
};

function IconByName({ name, size = 16 }: { name: IconName; size?: number }): React.JSX.Element {
  const C = (Icons as unknown as Record<string, (props: { size?: number }) => React.JSX.Element>)[name];
  return <C size={size} />;
}

export function Shortcuts({ role }: { role: Role }): React.JSX.Element {
  const navigate = useNavigate();
  const keys = SHORTCUTS_BY_ROLE[role];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {keys.map((k) => {
        // `keys` viene de SHORTCUTS_BY_ROLE; cada entrada existe en
        // ALL_SHORTCUTS por construcción. Si añades un rol con un key
        // nuevo, añádelo a ALL_SHORTCUTS o saltará a runtime.
        const def = ALL_SHORTCUTS[k];
        return (
          <button
            key={def.key}
            className="approval-queue-row"
            onClick={() => { void navigate(def.to); }}
          >
            <IconByName name={def.icon} />
            <div className="approval-queue-info">
              <div className="approval-queue-client">{def.title}</div>
              <div className="approval-queue-meta"><span>{def.hint}</span></div>
            </div>
            <Icons.ChevR size={12} />
          </button>
        );
      })}
    </div>
  );
}
